<script setup lang="ts">
import { computed, onMounted } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"

import { DateStrip } from "@/shared/ui"
import { buildRecordsDateStrip } from "../model/dateStrip"
import { useRecordsStore } from "../model/store"
import BookingActionsSheet from "./BookingActionsSheet.vue"
import CancelDialog from "./CancelDialog.vue"
import CellChooserSheet from "./CellChooserSheet.vue"
import CreateBookingSheet from "./CreateBookingSheet.vue"
import CreateTimeBlockSheet from "./CreateTimeBlockSheet.vue"
import DeleteTimeBlockDialog from "./DeleteTimeBlockDialog.vue"
import RescheduleSheet from "./RescheduleSheet.vue"
import TimeGrid from "./TimeGrid.vue"

defineOptions({ name: "RecordsView" })

const store = useRecordsStore()
const { t, d } = useI18n()
const route = useRoute()
const router = useRouter()

const dateStripItems = computed(() =>
  buildRecordsDateStrip(new Date(), store.salonTz, 2, 14, store.selectedDate),
)

function formatShort(value: string): string {
  // value is YYYY-MM-DD; parse as local date for display
  const [year, month, day] = value.split("-").map(Number)
  const dt = new Date(year, month - 1, day)
  return d(dt, { month: "short", day: "numeric" })
}

function formatLong(value: string): string {
  const [year, month, day] = value.split("-").map(Number)
  const dt = new Date(year, month - 1, day)
  return d(dt, { weekday: "long", month: "long", day: "numeric" })
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

onMounted(() => {
  const { create, master, date } = route.query
  const isCreate = create === "1"
  const isValidDate = typeof date === "string" && DATE_RE.test(date)

  if (isCreate && isValidDate) {
    store.selectDate(date)
  } else {
    void store.loadDay()
  }

  if (isCreate) {
    if (typeof master === "string" && master) {
      store.setActiveMaster(master)
    }
    store.openCreate()
    void router.replace({ path: route.path })
  }
})
</script>

<template>
  <div data-testid="records-view" class="records-view">
    <DateStrip
      :items="dateStripItems"
      :model-value="store.selectedDate"
      :ariaLabel="t('records.aria.dateStrip')"
      :format-short="formatShort"
      :format-long="formatLong"
      :today-label="t('journal.toolbar.today')"
      @update:model-value="store.selectDate"
    />

    <TimeGrid class="records-view__grid" />

    <!-- Sheets and dialogs — rendered outside the grid flow -->
    <BookingActionsSheet />
    <RescheduleSheet />
    <CancelDialog />
    <CellChooserSheet />
    <CreateBookingSheet />
    <CreateTimeBlockSheet />
    <DeleteTimeBlockDialog />
  </div>
</template>

<style scoped>
.records-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.records-view__grid {
  flex: 1;
  min-height: 0;
}
</style>
